import { useState, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { sanitizeUploadFileName } from './sanitize-upload-filename';

// ───────────────────────── Stall detection (DEV-478) ─────────────────────────
// A dropped connection does not reliably surface as an XHR `error` event: some
// browsers hold the socket open and simply stop making progress, so the chunk
// promise below would never settle and the row would sit on "Uploading"
// forever. XHR has no built-in progress-stall timeout (and a flat xhr.timeout
// would kill legitimately slow uploads), so each chunk arms its own watchdog
// that is reset on every progress event.
//
// Two windows, because a chunk has two phases with very different silence
// profiles: while the body is being sent we expect a steady stream of progress
// events, but once it is sent we are waiting on the server, which is silent by
// nature and may take a while to commit the final chunk.
const UPLOAD_STALL_TIMEOUT_MS = 30 * 1000; // no upload-progress events
const RESPONSE_STALL_TIMEOUT_MS = 2 * 60 * 1000; // bytes sent, awaiting server response

// Every connection-level failure message this hook reports starts with this prefix, so consumers
// can tell "the network died" apart from "the server rejected the file" and give the right advice.
// Exported rather than duplicated as a literal at the consumer. (DEV-478)
export const NETWORK_ERROR_PREFIX = 'Network error';

// ───────────────────────── Chunk outcome (DEV-746) ─────────────────────────
// The upload endpoint reports its own failures in the response body — `{"success": false, "error": ... }`
// — in addition to the status code. It used to answer 200 for those failures, and this hook branched on
// the status code alone, so a rejected chunk was reported as a successful upload: the row rendered as
// complete, the descriptor was persisted with isComplete:true, and the blob was missing or had already
// been deleted server-side. Submit and Download then failed much later with "did not upload properly"
// and "The specified blob does not exist".
//
// The server now sends a real failure status as well, so either signal alone is enough. Both are
// checked because the body check also covers any other endpoint still answering 200 with success:false.
// Returns the failure message, or null when the chunk genuinely succeeded.
const getChunkFailureMessage = (status: number, statusText: string, parsedResponse: unknown): string | null => {
    // parsedResponse is the raw response when the body was not JSON, so these reads are deliberately loose.
    const body = parsedResponse as { success?: boolean; error?: string } | null;
    const statusFailed = status < 200 || status >= 300;
    const bodyReportedFailure = body?.success === false;

    if (!statusFailed && !bodyReportedFailure) {
        return null;
    }

    // Prefer the server's own message: xhr.statusText is empty over HTTP/2, so it is the weaker source.
    return body?.error || statusText || 'Upload failed';
};

interface IUploaderOptions {
    // file upload endpoint
    endpoint: string;

    // file upload authentication token, automatically refreshes and adds to header if passed in
    getAuthToken: () => Promise<string>;

    // maximum number of simultaneous file uploads
    maxSimultaneousUploads?: number;

    events: {
        // true if the file is valid, false otherwise and don't add to uploads
        onFileValidation?: (fileName: string) => boolean | Promise<boolean>;

        // report file upload progress
        onProgress?: (id: string, fileName: string, progress: number) => void;

        // report file upload error
        onError?: (id: string, fileName: string, error: Error) => void;

        // report file upload success
        onComplete?: (id: string, fileName: string, response: any) => void;

        // report file upload start
        onStart?: (id: string, fileName: string, fileSizeKb: number) => void;

        // report all uploads completed
        onAllComplete?: (succeededFileIds: string[], failedFileIds: string[]) => void;

        // report files that were rejected because they are empty (zero-length) and were not queued for upload
        onEmptyFilesRejected?: (fileNames: string[]) => void;
    };
}

interface IFile {
    id: string;
    name: string;
    size: number;
    progress: number;
}

export interface IFileUploader {
    upload: (files: File[] | FileList) => string[];
    cancel: (id?: string) => void;
    retry: (id: string) => void;
    files: IFile[];
}

export const useFileUploader = (props: IUploaderOptions): IFileUploader => {
    const { endpoint, getAuthToken, events, maxSimultaneousUploads } = props;
    const { onFileValidation, onProgress, onError, onComplete, onStart, onAllComplete, onEmptyFilesRejected } = events;

    const [files, setFiles] = useState<IFile[]>([]);

    // ───────────────────────── Upload queue state ─────────────────────────
    // This hook implements a concurrency-limited upload queue. Files handed to
    // `upload()` are not uploaded immediately; instead they are pushed onto
    // `queueRef` and drained by `processQueue()`, which only ever keeps up to
    // `maxSimultaneousUploads` uploads in flight at once. Each upload is sent to
    // the server in fixed-size chunks (`CHUNK_SIZE`). The three refs below are
    // the moving parts of that queue and are kept in refs (not state) because
    // they are mutated synchronously during draining and must not trigger
    // re-renders or be captured stale by closures.

    // Live XMLHttpRequest objects keyed by file id, used to cancel/abort in-flight uploads.
    const requestsRef = useRef<Record<string, XMLHttpRequest>>({});
    // Number of uploads currently in flight. Acts as the "occupied slots" counter
    // that gates how many items processQueue may start (see maxSimultaneousUploads).
    const activeUploadsRef = useRef<number>(0);
    // Pending (not-yet-started) uploads waiting for a free slot.
    const queueRef = useRef<{ id: string; file: File }[]>([]);
    // Original File objects kept by id so a failed upload can be retried without
    // the user re-selecting the file. Entries are cleared on success/cancel and
    // retained on error (needed for retry).
    const uploadedFilesRef = useRef<Record<string, File>>({});
    // File ids currently being retried. Retries should reuse the already-accepted
    // upload and therefore must not re-run validation/file-limit/overwrite prompts.
    const retryingUploadIdsRef = useRef<Set<string>>(new Set());
    // True once at least one upload has actually been started. Used as the
    // "something was processed" guard for onAllComplete. This cannot be inferred
    // from requestsRef, because every terminal path (load/error/abort) deletes its
    // entry — after an all-failed batch requestsRef is empty, and keying off it
    // meant onAllComplete never fired. (DEV-478)
    const processedAnyRef = useRef<boolean>(false);
    const CHUNK_SIZE = 2 * 1024 * 1024; // 2MB

    // helper functions

    // Fires onAllComplete once the queue has fully drained: nothing in flight,
    // nothing waiting, and at least one request was actually processed. Called
    // after every upload finishes (and when validation rejects an item) so it
    // catches whichever path empties the queue last.
    const checkAllCompleted = () => {
        if (activeUploadsRef.current === 0 && processedAnyRef.current && queueRef.current.length === 0) {
            onAllComplete?.([], []); // TBD: properly track succeeded/failed if needed, but for now just preserving structure
            requestsRef.current = {};
            processedAnyRef.current = false;
        }
    };

    // Drains the queue while honoring the concurrency limit. Pulls items off the
    // front of queueRef and starts uploading them until either the queue is empty
    // or we've reached maxSimultaneousUploads in-flight uploads. This is called
    // both when new files are enqueued (from `upload`) and when an in-flight
    // upload finishes (from `uploadFileInChunks`), so a freed slot is immediately
    // refilled by the next waiting file.
    const processQueue = async () => {
        const maxSimultaneous = maxSimultaneousUploads || Number.MAX_SAFE_INTEGER;
        while (activeUploadsRef.current < maxSimultaneous && queueRef.current.length > 0) {
            const nextItem = queueRef.current.shift();
            if (nextItem) {
                const isRetry = retryingUploadIdsRef.current.has(nextItem.id);
                if (isRetry) {
                    retryingUploadIdsRef.current.delete(nextItem.id);
                }

                // update active number of uploads immediately to reserve the slot
                // (claim the slot before any awaits so concurrent drains don't oversubscribe)
                activeUploadsRef.current++;

                // validate the file in processQueue function instead of on push
                if (!isRetry && onFileValidation) {
                    let isValid = false;
                    try {
                        // Sanitize before validating. Every name the consumer has on record — local
                        // descriptors and the server's — was stored sanitized, so handing the raw
                        // File.name to a duplicate check compares unlike with unlike and the check can
                        // never match. That is why the overwrite prompt never fired for a name carrying
                        // any of the unsafe characters. (DEV-892)
                        isValid = await onFileValidation(sanitizeUploadFileName(nextItem.file.name));
                    } catch {
                        // The overwrite-confirm prompt *rejects* on Cancel rather than resolving false.
                        // Without this catch the rejection escapes the drain loop with the slot above
                        // already claimed, so the decrement below never runs: every cancelled overwrite
                        // permanently burned one of the concurrency slots and stranded whatever was still
                        // queued behind it, on top of raising an unhandled rejection. Treat a rejection as
                        // "not valid" and fall into the normal release-and-continue path. (DEV-744)
                        isValid = false;
                    }
                    if (!isValid) {
                        // if invalid, release the slot and try to process next item
                        activeUploadsRef.current--;
                        checkAllCompleted();
                        continue;
                    }
                }
                uploadFileInChunks(nextItem.file, nextItem.id);
            }
        }
    };

    // event functions

    // `onActivity` re-arms the caller's stall watchdog: a progress event is the only
    // positive evidence we get that the connection is still alive. (DEV-478)
    const addProgressEvent = (xhr: XMLHttpRequest, file: IFile, chunkIndex: number, totalSize: number, onActivity: () => void) => {
        xhr.upload.addEventListener('progress', (event) => {
            onActivity();
            if (event.lengthComputable) {
                const uploadedBeforeThisChunk = chunkIndex * CHUNK_SIZE;
                const totalUploaded = uploadedBeforeThisChunk + event.loaded;
                const progress = Math.min(Math.round((totalUploaded / totalSize) * 100), 100);
                onProgress?.(file.id, file.name, progress);
            }
        });
    };

    const uploadFileInChunks = async (fileToUpload: File, id: string) => {
        // Uploads a single file as a sequence of CHUNK_SIZE byte ranges. Chunks are
        // sent strictly one-at-a-time (each await blocks the loop) so the server
        // receives them in order; on the final chunk we surface onComplete. When the
        // file finishes (or aborts/fails) we release the concurrency slot
        // (activeUploadsRef--) and call processQueue() to start the next waiting file.
        // calculate chunk size
        const totalSize = fileToUpload.size;
        const totalChunks = Math.max(1, Math.ceil(totalSize / CHUNK_SIZE));

        // RQ-148 (DEV-496): the name sent to the server (and used for the stored blob) must have
        // URL/storage-unsafe characters replaced with '_'.
        // DEV-892: this is also the only name that leaves this hook. Consumers key their duplicate
        // checks off the names they already hold, which are sanitized, so every outbound callback
        // below reports safeName rather than fileToUpload.name — otherwise those checks compare a raw
        // name against sanitized ones and silently miss.
        const safeName = sanitizeUploadFileName(fileToUpload.name);

        // add file to local array
        const file = { id, name: safeName, size: totalSize, progress: 0 };
        setFiles((prev) => {
            const existingFileIndex = prev.findIndex((existingFile) => existingFile.id === id);
            if (existingFileIndex >= 0) {
                return prev.map((existingFile) => (existingFile.id === id ? file : existingFile));
            }

            return [...prev, file];
        });

        // report file upload start
        onStart?.(id, safeName, totalSize / 1024);

        // track if file is cancelled
        let isCancelled = false;

        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
            if (isCancelled) break;

            // set up chunk props
            const start = chunkIndex * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, totalSize);
            const chunk = fileToUpload.slice(start, end);
            const chunkSize = end - start;

            const success = await new Promise<boolean>(async (resolve) => {
                // create XHR request for upload and add to requests array
                const xhr = new XMLHttpRequest();
                requestsRef.current[id] = xhr;
                processedAnyRef.current = true;

                // ───────────── stall detection / single-settle bookkeeping (DEV-478) ─────────────
                // Every terminal path funnels through `settle`, which is idempotent: the watchdog
                // aborts the request, and that abort then fires the abort listener, so more than
                // one path routinely tries to finish the same chunk. Before this, the `error`
                // listener resolved nothing at all, so a network failure left this promise pending
                // forever — the concurrency slot below was never released and every later upload
                // (including Retry) sat in the queue unstarted.
                let stallTimer: ReturnType<typeof setTimeout> | undefined;
                let didTimeOut = false;
                let settled = false;

                const settle = (result: boolean) => {
                    if (settled) {
                        return;
                    }
                    settled = true;
                    if (stallTimer) {
                        clearTimeout(stallTimer);
                        stallTimer = undefined;
                    }
                    window.removeEventListener('offline', handleOffline);
                    resolve(result);
                };

                const failNow = (message: string) => {
                    if (settled) {
                        return;
                    }
                    // Set before aborting: the abort listener treats an abort as a user cancel, and
                    // this one is a failure we are reporting ourselves.
                    didTimeOut = true;
                    delete requestsRef.current[id];
                    onError?.(id, safeName, new Error(message));
                    settle(false);
                    xhr.abort();
                };

                const armWatchdog = (timeoutMs: number) => {
                    if (settled) {
                        return;
                    }
                    if (stallTimer) {
                        clearTimeout(stallTimer);
                    }
                    stallTimer = setTimeout(() => failNow(`${NETWORK_ERROR_PREFIX} - upload stalled`), timeoutMs);
                };

                // Function declaration (not a const) so `settle` above can unregister it.
                // Turning off WiFi fires this immediately, so the row flips to Failed at once
                // rather than after the watchdog window; the watchdog still covers the silent
                // stalls the OS does not report (captive portals, half-open sockets).
                function handleOffline() {
                    failNow(`${NETWORK_ERROR_PREFIX} - connection lost`);
                }
                window.addEventListener('offline', handleOffline);

                // add event listeners for progress, error, and abort
                addProgressEvent(xhr, file, chunkIndex, totalSize, () => armWatchdog(UPLOAD_STALL_TIMEOUT_MS));

                // Once the body is sent, progress events stop and we are waiting on the server,
                // which is legitimately silent while it commits the chunk — so switch to the
                // longer window rather than failing a slow-but-healthy upload.
                xhr.upload.addEventListener('loadend', () => armWatchdog(RESPONSE_STALL_TIMEOUT_MS));

                xhr.addEventListener('error', () => {
                    delete requestsRef.current[id];
                    onError?.(id, safeName, new Error(NETWORK_ERROR_PREFIX));
                    settle(false);
                });

                xhr.addEventListener('abort', () => {
                    delete requestsRef.current[id];
                    if (!didTimeOut) {
                        isCancelled = true;
                    }
                    settle(false);
                });

                // Arm before awaiting the token: acquiring it is itself a network call, so a
                // connection lost at this point would otherwise hang here, before any XHR event
                // could fire.
                armWatchdog(UPLOAD_STALL_TIMEOUT_MS);

                // add event listener for load in order to determine if upload is complete
                xhr.addEventListener('load', () => {
                    delete requestsRef.current[id];

                    // Parse the body for EVERY chunk, not just the last one: a mid-file chunk can be
                    // rejected too, and before DEV-746 the rejection was invisible because the body was
                    // only ever read on the final chunk. Keep the raw response as the fallback so a
                    // non-JSON body still reaches onComplete unchanged.
                    let response = xhr.response;
                    try {
                        response = JSON.parse(xhr.responseText);
                    } catch (e) {
                        // ignore parse error, use raw response
                    }

                    const failureMessage = getChunkFailureMessage(xhr.status, xhr.statusText, response);
                    if (failureMessage) {
                        onError?.(id, safeName, new Error(failureMessage));
                        settle(false);
                        return;
                    }

                    if (chunkIndex === totalChunks - 1) {
                        // upload finished successfully; the retained File is no longer needed
                        delete uploadedFilesRef.current[id];
                        onComplete?.(id, safeName, response);
                    }
                    settle(true);
                });

                // Everything from here on is wrapped: this is an async Promise executor, so a throw
                // would be swallowed as an unhandled rejection and the promise would hang forever —
                // the same "stuck on Uploading" failure mode the watchdog above exists to prevent.
                // Acquiring the token is itself a network call, so this is a live path when offline.
                try {
                    // get auth token and open XHR request
                    const token = await getAuthToken();

                    // the watchdog (or an offline event) may have failed this chunk while the token
                    // request was outstanding — don't send a request for an already-failed chunk
                    if (settled) {
                        return;
                    }

                    xhr.open('POST', endpoint);
                    xhr.setRequestHeader('Authorization', `Bearer ${token}`);

                    // append form data to chunk upload
                    const formData = new FormData();
                    formData.append('qqfile', chunk);
                    formData.append('qqfilename', safeName);
                    formData.append('qqpartindex', chunkIndex.toString());
                    formData.append('qqpartbyteoffset', start.toString());
                    formData.append('qqchunksize', chunkSize.toString());
                    formData.append('qqtotalfilesize', totalSize.toString());

                    // send XHR request
                    xhr.send(formData);
                } catch (e) {
                    failNow(`Unable to start upload: ${e instanceof Error ? e.message : String(e)}`);
                }
            });

            if (!success) break;
        }

        activeUploadsRef.current--;
        processQueue();
        checkAllCompleted();
    };

    // public functions

    // Entry point for callers. Filters out empty files, assigns each remaining
    // file a unique id, enqueues it on queueRef, then kicks off processQueue to
    // start uploading subject to the concurrency limit. Returns the generated ids
    // synchronously so callers can correlate later progress/complete/error events.
    const upload = (filesToUpload: File[] | FileList): string[] => {
        const fileIds: string[] = [];
        const filesToProcess = filesToUpload instanceof FileList ? Array.from(filesToUpload) : filesToUpload;

        // reject zero-length (empty) files - they are not queued for upload, but valid files still proceed
        const emptyFileNames: string[] = [];

        for (const fileToUpload of filesToProcess) {
            if (fileToUpload.size === 0) {
                // Raw name on purpose, unlike every other outbound name in this hook (DEV-892): a
                // zero-byte file never enters the system, so there is no stored counterpart for this
                // name to match and nothing downstream compares it. Echoing what the user picked is
                // the more useful message.
                emptyFileNames.push(fileToUpload.name);
                continue;
            }

            // generate unique id
            const id = uuidv4();

            // add file id to local array
            fileIds.push(id);

            // retain the original File so the upload can be retried later if it fails
            uploadedFilesRef.current[id] = fileToUpload;

            // add to queue
            queueRef.current.push({ id, file: fileToUpload });
        }

        // notify caller of any rejected empty files so the user can be informed
        if (emptyFileNames.length > 0) {
            onEmptyFilesRejected?.(emptyFileNames);
        }

        // trigger queue processing
        processQueue();

        return fileIds;
    };

    // Cancels uploads. With an id, aborts that file's in-flight request (if any)
    // and removes it from the queue if it hasn't started yet. Without an id,
    // aborts everything in flight and clears the entire queue.
    const cancel = (id?: string) => {
        if (id) {
            // Cancel specific file
            if (requestsRef.current[id]) {
                requestsRef.current[id].abort();
                delete requestsRef.current[id];
            }
            // Also remove from queue if not started yet
            queueRef.current = queueRef.current.filter((f) => f.id !== id);
            // drop the retained File; a cancelled upload is not retryable
            delete uploadedFilesRef.current[id];
        } else {
            // Cancel all
            Object.values(requestsRef.current).forEach((xhr) => xhr.abort());
            requestsRef.current = {};
            queueRef.current = [];
            uploadedFilesRef.current = {};
        }
    };

    // Retries a previously failed upload by re-enqueuing the original File under the
    // same id, so the existing descriptor/uploaderId correlation holds and the server
    // overwrites the prior (partial) upload. No-op if the File is no longer retained.
    const retry = (id: string) => {
        const file = uploadedFilesRef.current[id];
        if (!file) {
            return;
        }
        // avoid duplicate enqueue if a retry is already pending for this id
        if (!queueRef.current.some((item) => item.id === id)) {
            retryingUploadIdsRef.current.add(id);
            queueRef.current.push({ id, file });
        }
        processQueue();
    };

    return {
        upload,
        cancel,
        retry,
        files
    };
};
